import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { useMobileRotation } from 'src/contexts/MobileRotationContext';
import { MobileHeader } from './MobileHeader';
import { SidebarLogo } from 'src/Layout/Sidebar/styles';
import MobileRotationModal from 'src/Layout/MobileRotationModal';
import { getSidebarItems } from 'src/Layout/Sidebar/constants';
import { isBhiAdmin } from 'src/helpers/permissions';
import { selectProfile } from 'src/modules/profile/slices';

interface MobileWrapperProps {
  children: React.ReactNode;
}

export const MobileWrapper: React.FC<MobileWrapperProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const settings = useSelector(getApplicationSettings);
  const { profile } = useSelector(selectProfile);
  const { showRotationModal, hideRotationModal, isDismissed, setIsDismissed } = useMobileRotation();

  // Pages where rotation modal should NOT be shown
  const excludedPages = ['/', '/home', '/inbox'];

  // Reset dismissal state when navigating to a new page
  useEffect(() => {
    setIsDismissed(false);
  }, [location.pathname, setIsDismissed]);



  useEffect(() => {
    const checkScreenSizeAndOrientation = () => {
      const isSmallScreen = window.innerWidth < 768;
      const isPortrait = window.innerHeight > window.innerWidth;
      const isExcludedPage = excludedPages.includes(location.pathname);
      
      // Hide modal if on excluded page
      if (isExcludedPage) {
        hideRotationModal();
        return;
      }

      const shouldShowModal = isSmallScreen && isPortrait && !isDismissed;

      if (shouldShowModal) {
        const timer = setTimeout(() => {
          showRotationModal();
        }, 100);

        return () => clearTimeout(timer);
      } else if (isSmallScreen && !isPortrait) {
        // Auto-hide modal when device is rotated to landscape
        hideRotationModal();
      }
    };

    checkScreenSizeAndOrientation();

    const handleResize = () => {
      checkScreenSizeAndOrientation();
    };

    const handleOrientationChange = () => {
      setTimeout(checkScreenSizeAndOrientation, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [showRotationModal, hideRotationModal, isDismissed, location.pathname]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const drawer = (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="div">
          Navigation
        </Typography>
      </Box>
      <List>
        {getSidebarItems(isBhiAdmin(profile)).map(item => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (isMobile && window.innerWidth < 768 && window.innerHeight > window.innerWidth && !isDismissed && !excludedPages.includes(location.pathname)) {
    return <MobileRotationModal />;
  }

  if (!isMobile) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'black',
        }}
      >
        <Toolbar sx={{maxWidth: '95vw'}}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, color: 'black' }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              flexGrow: 1,
              color: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Link to="/home">
              <Box sx={{ display: 'flex', height: '40px' }}>
                <SidebarLogo
                  src={settings?.logo}
                  style={{
                    width: '100px',
                    margin: '0',
                    minHeight: '40px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Link>
          </Box>
          <MobileHeader />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '80%',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: isMobile ? 0 : 3,
          py: isMobile ? 0 : 3,
          width: { sm: `calc(100% - 0px)` },
          mt: '64px',
        }}
      >
        {children}
      </Box>

    </Box>
  );
};
